import { BaseButton } from "components/ui/Button";
import Icon from "components/ui/Icon";
import Tile from "components/ui/Tile";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { OnboardingEventMessageType } from "types/onboarding";
import { mediaMax } from "utils/theme";
import Modal, { ModalApi } from "./Modal";

const Wrapper = styled(Tile)`
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 440px;
  padding: 0;
  overflow: hidden;

  text-align: left;

  pointer-events: all;

  ${mediaMax("md")} {
    max-width: 100%;
    height: 100%;

    border-radius: 0;
    box-shadow: none;
  }
`;

const Iframe = styled.iframe`
  height: calc(100vh - 96px);

  border: none;

  ${mediaMax("md")} {
    height: 100%;
  }
`;

const CloseButton = styled(BaseButton)`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
`;

interface Props {
  onSuccess?: () => void;
  onClose?: () => void;
}

const OnboardingModal = (
  { onSuccess, onClose }: Props,
  forwardedRef: React.ForwardedRef<unknown>
) => {
  const ref = useRef<ModalApi>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const reset = () => {
    setIsSuccess(false);
  };

  const handleSuccess = () => {
    setIsSuccess(true);
  };

  const handleClose = useCallback(() => {
    if (isSuccess) onSuccess?.();
    onClose?.();
    ref.current?.dismiss();

    reset();
  }, [isSuccess, onSuccess, onClose]);

  const handleMessage = useCallback(
    (event: MessageEvent<{ type: OnboardingEventMessageType }>) => {
      switch (event.data.type) {
        case OnboardingEventMessageType.exit:
          handleClose();
          break;
        case OnboardingEventMessageType.success:
          handleSuccess();
          break;
        default:
      }
    },
    [handleClose]
  );

  useImperativeHandle(forwardedRef, () => ref.current);

  useEffect(() => {
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);

  return (
    <Modal ref={ref} hideDefaultUi>
      <Wrapper>
        <Iframe
          src="/onboarding?hideNavigation=true&embedded=true"
          title="Onboarding"
        />

        <CloseButton onClick={handleClose}>
          <Icon name="close" />
        </CloseButton>
      </Wrapper>
    </Modal>
  );
};

export default forwardRef(OnboardingModal);
